/**
 * La roue du tirer-pour-rafraîchir ne tourne qu'après un tirer.
 *
 * Le `refreshing` qu'une page reçoit est celui de sa relecture, quelle qu'elle soit : il passe aussi à vrai
 * quand l'écran relit au focus, ou quand la coquille relit le compte. Passé tel quel au `RefreshControl`, la
 * roue native tournait sans que personne ait tiré (et iOS décalait le contenu le temps de la relecture). Ce
 * crochet ne la montre que du tirer jusqu'à la fin de la relecture qu'il a lancée :
 *  - au tirer : la roue tourne, `onRefresh` part ;
 *  - la relecture commence (`refreshing` vrai), puis finit (faux) : la roue s'arrête ;
 *  - une relecture qui ne commence pas (rien à relire, déjà en cours) n'immobilise pas la roue : elle s'arrête
 *    après `PULL_SETTLE_MS`.
 *
 * Au web, pas de geste (`onRefresh` rendu absent) : un pointeur ne tire pas une page, et la flèche du header
 * y reste le moyen de relire.
 */
import { useCallback, useEffect, useState } from "react"
import { Platform } from "react-native"

/** Le temps laissé à une relecture pour commencer après un tirer. */
export const PULL_SETTLE_MS = 1500

/** Hors d'un tirer ; tiré, la relecture pas encore commencée ; la relecture lancée par le tirer en cours. */
type PullPhase = "idle" | "pulled" | "started"

export function usePulledRefresh(refreshing: boolean, onRefresh: (() => void) | undefined) {
    const [phase, setPhase] = useState<PullPhase>("idle")

    useEffect(() => {
        if (phase === "pulled" && refreshing) setPhase("started")
        else if (phase === "started" && !refreshing) setPhase("idle")
    }, [phase, refreshing])

    useEffect(() => {
        if (phase !== "pulled") return
        const timer = setTimeout(() => setPhase((current) => (current === "pulled" ? "idle" : current)), PULL_SETTLE_MS)
        return () => clearTimeout(timer)
    }, [phase])

    const pull = useCallback(() => {
        setPhase("pulled")
        onRefresh?.()
    }, [onRefresh])

    const gesture = Platform.OS !== "web" && onRefresh != null
    return { refreshing: gesture && phase !== "idle", onRefresh: gesture ? pull : undefined }
}
